import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { BER_OPCIOK } from '../ber.opciok';
import { naploNyomtat, NyomtatasOszlop } from '../../naplo/naplo-nyomtatas';
import { AdatszolgApi, csvLetolt, evLista, ftSzoveg, HONAPNEV } from './adatszolg.api';

// BÉR – Bérösszesítő (adott időszakra) – RLB: Bér adatszolgáltatás → Bérösszesítő (adott időszakra).
// Tetszőleges hónaptól hónapig (évhatáron is át) a TÁROLT számfejtések összesítve, dolgozónként,
// hónaponként vagy jogviszony-típusonként. Nyomtatás a Könyvelés rács-nyomtatójával, CSV-export.
@Component({
  selector: 'app-ber-berosszesito',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './berosszesito.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './adatszolg.component.scss'],
})
export class BerosszesitoComponent implements OnInit {
  evek = evLista();
  honapok = BER_OPCIOK.honapok;
  csoportok = [{ k: 'alkalmazott', c: 'Dolgozónként' }, { k: 'ho', c: 'Hónaponként' }, { k: 'jogviszony', c: 'Jogviszony szerint' }];

  tolEv = new Date().getFullYear(); tolHo = 1;
  igEv = new Date().getFullYear(); igHo = 12;
  csoport: 'alkalmazott' | 'ho' | 'jogviszony' = 'alkalmazott';
  csakVegleges = false;
  adat: any = null;
  tolt = false;

  constructor(private ber: BerService, private api: AdatszolgApi, private toast: ToastController) {}

  get aktiv() { return this.ber.aktiv; }

  async ngOnInit() {
    await this.ber.init();
    this.tolEv = this.igEv = this.aktiv.ev;
    await this.betolt();
  }

  /** Gyors időszakok: a munka éve, az előző év, és az aktív hónappal záruló 12 hónap. */
  gyors(mit: 'ev' | 'elozo' | '12') {
    const ev = this.aktiv.ev;
    if (mit === 'ev') { this.tolEv = ev; this.tolHo = 1; this.igEv = ev; this.igHo = 12; }
    else if (mit === 'elozo') { this.tolEv = ev - 1; this.tolHo = 1; this.igEv = ev - 1; this.igHo = 12; }
    else {
      const ig = new Date(ev, this.aktiv.ho - 1, 1); const tol = new Date(ev, this.aktiv.ho - 12, 1);
      this.tolEv = tol.getFullYear(); this.tolHo = tol.getMonth() + 1; this.igEv = ig.getFullYear(); this.igHo = ig.getMonth() + 1;
    }
    this.betolt();
  }

  private hh(n: number) { return String(n).padStart(2, '0'); }
  get idoszakNev(): string {
    return `${this.tolEv}. ${HONAPNEV[this.tolHo - 1]} – ${this.igEv}. ${HONAPNEV[this.igHo - 1]}`;
  }
  get csoportNev(): string { return this.csoportok.find((c) => c.k === this.csoport)?.c || ''; }
  get forditott(): boolean { return this.tolEv * 100 + this.tolHo > this.igEv * 100 + this.igHo; }

  szabOssz(k: string): number { return (this.adat?.szabadsag?.sorok || []).reduce((a: number, r: any) => a + (Number(r[k]) || 0), 0); }

  async betolt() {
    if (!this.aktiv.ugyfelId) return;
    if (this.forditott) { this.adat = null; return; }
    this.tolt = true;
    try {
      const q = `?tol=${this.tolEv}-${this.hh(this.tolHo)}&ig=${this.igEv}-${this.hh(this.igHo)}&csoport=${this.csoport}${this.csakVegleges ? '&csak_vegleges=1' : ''}`;
      this.adat = await this.api.get('/berosszesito/' + this.aktiv.ugyfelId + q);
    } catch (e: any) { this.adat = null; this.hiba(this.api.hibaUzenet(e, 'Nem sikerült betölteni a bérösszesítőt.')); }
    finally { this.tolt = false; }
  }

  /** Csak a nem üres oszlopok (KIVA, egyéb levonás, SZJA-mentes jogcím csak ha van). */
  get oszlopok(): NyomtatasOszlop[] {
    const o = this.adat?.osszesen || {};
    const l: NyomtatasOszlop[] = [
      { k: 'cim', c: this.csoport === 'alkalmazott' ? 'Dolgozó' : (this.csoport === 'ho' ? 'Hónap' : 'Jogviszony'), szeles: true },
      { k: 'db', c: 'Számf.', num: true },
      { k: this.csoport === 'alkalmazott' ? 'honap' : 'fo', c: this.csoport === 'alkalmazott' ? 'Hónap' : 'Fő', num: true },
      { k: 'brutto', c: 'Bruttó', num: true },
    ];
    if (o.nem_szja) l.push({ k: 'nem_szja', c: 'SZJA-mentes jogcím', num: true });
    l.push({ k: 'adoalap_kedvezmeny', c: 'Adóalap-kedvezmény', num: true, cs: 'cs-afa' },
      { k: 'adoalap', c: 'SZJA-alap', num: true, cs: 'cs-afa' },
      { k: 'szja', c: 'SZJA', num: true, cs: 'cs-afa' },
      { k: 'tb_jarulek', c: 'TB-járulék', num: true, cs: 'cs-penz' },
      { k: 'csaladi_jarulekkedv', c: 'Csal. járulékkedv.', num: true, cs: 'cs-penz' },
      { k: 'letiltas', c: 'Letiltás', num: true });
    if (o.egyeb_levonas) l.push({ k: 'egyeb_levonas', c: 'Egyéb levonás', num: true });
    l.push({ k: 'netto', c: 'Nettó', num: true });
    if (o.szocho_13 || !o.kiva) l.push({ k: 'szocho_13', c: 'Szocho', num: true });
    if (o.kiva) l.push({ k: 'kiva', c: 'KIVA', num: true });
    l.push({ k: 'koltseg', c: 'Összköltség', num: true });
    return l;
  }

  ertek(sor: any, o: NyomtatasOszlop): string {
    const v = sor[o.k];
    if (o.k === 'cim') return sor.cim + (sor.alcim ? ` (${sor.alcim})` : '');
    if (o.k === 'db' || o.k === 'fo' || o.k === 'honap') return v == null ? '' : String(v);
    return o.num ? ftSzoveg(v) : String(v ?? '');
  }

  nyomtat() {
    if (!this.adat) return;
    const u = this.adat.ugyfel || {};
    const oszl = this.oszlopok;
    const ossz = this.adat.osszesen;
    naploNyomtat({
      cim: 'Bérösszesítő (adott időszakra)',
      ugyfel: { nev: u.nev, adoszam: u.adoszam, cim: u.cim },
      mod: 'Bérszámfejtés',
      szurok: [['Időszak', this.idoszakNev], ['Bontás', this.csoportNev], ['Számfejtések', this.csakVegleges ? 'csak véglegesített' : 'minden (tervezet is)']],
      oszlopok: oszl,
      sorok: this.adat.sorok,
      ertek: (r, o) => this.ertek(r, o),
      osszegSor: (o) => (o.k === 'cim' ? 'Összesen' : this.ertek(ossz, o)),
    });
  }

  csv() {
    if (!this.adat) return;
    const oszl = this.oszlopok;
    const szam = (r: any, o: NyomtatasOszlop) => (o.k === 'cim' ? r.cim : (Number(r[o.k]) || 0));
    const sorok = this.adat.sorok.map((r: any) => [...oszl.map((o) => szam(r, o)), ...(this.csoport === 'alkalmazott' ? [r.alcim || ''] : [])]);
    sorok.push([...oszl.map((o) => (o.k === 'cim' ? 'Összesen' : (Number(this.adat.osszesen[o.k]) || 0))), ...(this.csoport === 'alkalmazott' ? [''] : [])]);
    const fej = [...oszl.map((o) => o.c), ...(this.csoport === 'alkalmazott' ? ['Adóazonosító'] : [])];
    csvLetolt(`berosszesito_${this.tolEv}${this.hh(this.tolHo)}-${this.igEv}${this.hh(this.igHo)}_${this.csoport}.csv`, fej, sorok);
  }

  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3000, color: 'danger' })).present(); }
}
